import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Warning } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

/**
 * Global "You are impersonating X" banner. Rendered inside AppLayout so it
 * appears on every route while the impersonation cookie is active.
 *
 * We infer active impersonation from the presence of an `x-impersonation`
 * signal — which we set on window whenever POST /superadmin/impersonate
 * succeeds. We ALSO derive it from user.role !== 'superadmin' on the
 * default workspace if the local storage flag is set (survives reloads).
 */
export default function ImpersonationBanner() {
    const { user, setUser } = useAuth();
    const nav = useNavigate();
    const [impersonation, setImpersonation] = useState(() => {
        try {
            const raw = localStorage.getItem("naukri:impersonation");
            return raw ? JSON.parse(raw) : null;
        } catch { return null; }
    });

    useEffect(() => {
        const handler = (e) => {
            const p = e?.detail || null;
            setImpersonation(p);
            if (p) localStorage.setItem("naukri:impersonation", JSON.stringify(p));
            else localStorage.removeItem("naukri:impersonation");
        };
        window.addEventListener("naukri:impersonation", handler);
        return () => window.removeEventListener("naukri:impersonation", handler);
    }, []);

    // No auto-clear: state is only cleared by the explicit exit() handler below.
    // (Auto-clear caused a race condition where the effect fired between
    // dispatchEvent and setUser, wiping fresh impersonation state.)

    if (!impersonation) return null;

    const exit = async () => {
        // Optimistically clear the visible impersonation state so the button
        // never appears "dead" — even if the network is flaky.
        localStorage.removeItem("naukri:impersonation");
        setImpersonation(null);
        window.dispatchEvent(new CustomEvent("naukri:impersonation", { detail: null }));
        try {
            const r = await api.post("/superadmin/impersonate/exit");
            if (r.data?.restore_required) {
                // Recovery cookie expired — force superadmin to log in again.
                toast.info(r.data.message || "Impersonation expired — please sign in as superadmin again.");
                nav("/login", { replace: true });
                return;
            }
            // Refresh /auth/me so the shell re-renders as superadmin.
            const me = await api.get("/auth/me");
            setUser(me.data);
            toast.success("Exited impersonation.");
            nav("/superadmin", { replace: true });
        } catch (err) {
            // Network failure — surface, but don't restore the banner (we
            // already cleared it optimistically so the user isn't stuck).
            toast.error(err?.response?.data?.detail || "Couldn't exit cleanly — please refresh.");
        }
    };

    return (
        <div
            data-testid="impersonation-banner"
            className="sticky top-0 z-40 bg-splash text-ink border-b-2 border-ink flex items-center gap-3 px-4 py-2.5 text-xs font-black uppercase tracking-wider"
        >
            <Warning size={16} weight="fill" />
            <span className="flex-1 truncate">
                Impersonating <span className="underline decoration-2 underline-offset-2">{impersonation.email}</span>
                {" "}({impersonation.role}) in workspace <span className="font-mono">{impersonation.workspace_id}</span>
                {" · session auto-exits in 30 min"}
            </span>
            <button
                onClick={exit}
                data-testid="impersonation-exit-btn"
                className="inline-flex items-center gap-1 bg-ink text-splash px-3 py-1 hover:bg-black transition-colors"
            >
                <X size={12} weight="bold" /> Exit
            </button>
        </div>
    );
}
